/**
 * Quien el asistente pasó al equipo y nadie ha escrito.  (spec 032, 036, 037)
 * El hilo de cada franja se vuelve a pedir mientras se mira (033 · FR-006).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { ControlDelHilo } from '../../components/ControlDelHilo'
import { useAbrirHilo } from '../../components/HilosFlotantes'
import { MensajesConversacion } from '../../components/MensajesConversacion'
import { NotasCliente } from '../../components/NotasCliente'
import { api } from '../../lib/api'
import { describirCita, llevaEsperando, masReciente, textoAlEquipo, textoVentana, ultimoClienteDeMensajes } from '../../lib/elevadas'
import { useFichaNegocio } from '../../lib/ficha-negocio'
import { cuandoCita, fechaHora } from '../../lib/tiempo'
import { useRefrescoHiloAbierto } from '../../lib/use-refresco-hilo'
import { useIncluirTestsVista } from '../../lib/use-incluir-tests'
import type { SobreMensajes } from '../../lib/tipos'
import { useEsAdminUi } from '../../ver-como'

interface Item {
    conversationId: number
    persona: string | null
    telefono: string | null
    correo: string | null
    team: string | null
    contactId: number | null
    handoverAt: string | null
    ultimoClienteAt: string | null
    cita: {
        estado: 'reserva' | 'cancelacion' | 'modificacion' | 'ninguna'
        fecha: string | null
        hora: string | null
        servicio: string | null
        profesional: string | null
    }
    intencion: string | null
    motivo: string | null
}

interface Lista {
    accountId: number
    ok: boolean
    motivo: string | null
    items: Item[]
}

interface Hilo {
    conversationId: number
    persona: string | null
    chatwootUrl: string | null
    messages: SobreMensajes
    mando: 'agendify' | 'equipo' | 'cerrada' | null
}

export function ElevadasNegocio() {
    const { accountId } = useParams<{ accountId: string }>()
    const esAdmin = useEsAdminUi()
    const { esDemo } = useFichaNegocio(accountId)
    const { incluirTests } = useIncluirTestsVista(accountId, esDemo, esAdmin)
    const qc = useQueryClient()

    const q = useQuery<Lista>({
        queryKey: ['elevadas', accountId, incluirTests],
        queryFn: () =>
            api.get<Lista>(
                `/business/${accountId}/elevadas?incluirTests=${incluirTests ? '1' : '0'}`,
            ),
        enabled: Boolean(accountId),
    })

    const items = q.data?.ok ? q.data.items : []
    const abrirHilo = useAbrirHilo()

    return (
        <div className="space-y-5 font-sans">
            <header>
                <h2 className="text-lg font-semibold text-neutral-100">Al equipo</h2>
                <p className="mt-1 text-sm text-neutral-400">
                    El asistente las pasó al equipo. Se puede seguir escribiendo
                    después de la primera respuesta. Sale por el mismo chat.
                </p>
                {q.data?.ok && (
                    <p className="mt-2 text-sm tabular-nums text-neutral-200">
                        {items.length === 0 ? 'Nadie pide al equipo ahora.' : textoAlEquipo(items.length)}
                    </p>
                )}
                {q.data && !q.data.ok && (
                    <p className="mt-2 text-sm text-amber-300">
                        No se pudo leer el canal{q.data.motivo ? `: ${q.data.motivo}` : '.'}
                    </p>
                )}
            </header>

            {q.isPending && <p className="text-sm text-neutral-500">Cargando…</p>}
            {q.isError && <p className="text-sm text-rose-400">No se pudo cargar esta lista.</p>}

            {items.length > 0 && (
                <div className="space-y-4">
                    {items.map((i) => (
                        <FranjaElevada
                            key={i.conversationId}
                            accountId={accountId!}
                            item={i}
                            onAbrir={() => accountId && abrirHilo(Number(accountId), i.conversationId)}
                            onEnviado={() => {
                                void qc.invalidateQueries({ queryKey: ['elevadas', accountId] })
                                void qc.invalidateQueries({ queryKey: ['business', accountId] })
                            }}
                        />
                    ))}
                </div>
            )}

        </div>
    )
}

function FranjaElevada({
    accountId,
    item,
    onAbrir,
    onEnviado,
}: {
    accountId: string
    item: Item
    onAbrir: () => void
    onEnviado: () => void
}) {
    const [visto, setVisto] = useState<string | null>(null)
    return (
        <article
            data-testid="franja-al-equipo"
            className="rounded-xl border border-neutral-800 bg-neutral-950"
        >
            <div className="grid lg:grid-cols-[minmax(16rem,3fr)_minmax(0,7fr)]">
                <PersonaElevada
                    accountId={accountId}
                    item={item}
                    onAbrir={onAbrir}
                    ventanaAt={masReciente(item.ultimoClienteAt, visto)}
                />
                <HiloElevada
                    accountId={accountId}
                    conversationId={item.conversationId}
                    onEnviado={onEnviado}
                    onClienteAt={setVisto}
                />
            </div>
        </article>
    )
}

function PersonaElevada({
    accountId,
    item,
    onAbrir,
    ventanaAt,
}: {
    accountId: string
    item: Item
    onAbrir: () => void
    ventanaAt: string | null
}) {
    return (
        <section className="min-w-0 space-y-4 border-b border-neutral-800 p-4 lg:border-b-0 lg:border-r">
            <header>
                <h3 className="text-base font-semibold text-neutral-100">
                    {item.persona ?? `Conversación #${item.conversationId}`}
                </h3>
                <VentanaCliente at={ventanaAt} />
                <button type="button" onClick={onAbrir} className="mt-2 text-xs text-sky-400 hover:text-sky-300">
                    Abrir en ventana
                </button>
                {item.handoverAt && (
                    <p
                        data-testid="reloj-espera"
                        className="mt-1 text-xs font-medium tabular-nums text-neutral-400"
                    >
                        <RelojEspera at={item.handoverAt} />
                    </p>
                )}
            </header>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-3 text-sm sm:grid-cols-2">
                <Dato etiqueta="Teléfono" valor={item.telefono} vacio="Sin teléfono" />
                <Dato etiqueta="Correo" valor={item.correo} vacio="Sin correo" />
                <Dato etiqueta="Cita" valor={describirCita(item.cita, cuandoCita)} />
                <Dato
                    etiqueta="Traspaso"
                    valor={item.handoverAt ? fechaHora(item.handoverAt) : null}
                    vacio="Sin hora"
                />
                {item.cita.estado === 'ninguna' && item.intencion && (
                    <Dato etiqueta="Qué quería" valor={item.intencion} />
                )}
                {item.cita.estado === 'ninguna' && item.motivo && (
                    <Dato etiqueta="Por qué al equipo" valor={item.motivo} />
                )}
            </dl>
            {item.contactId !== null ? (
                <NotasCliente accountId={accountId} contactId={item.contactId} compacto />
            ) : (
                <p className="text-xs text-neutral-600">Sin contacto en el canal: no se pueden añadir notas.</p>
            )}
        </section>
    )
}

function Dato({
    etiqueta,
    valor,
    vacio,
}: {
    etiqueta: string
    valor: string | null
    vacio?: string
}) {
    return (
        <div>
            <dt className="text-[11px] uppercase tracking-wide text-neutral-500">{etiqueta}</dt>
            <dd className={valor ? 'mt-0.5 text-neutral-200' : 'mt-0.5 text-neutral-600'}>
                {valor ?? vacio ?? '—'}
            </dd>
        </div>
    )
}

function VentanaCliente({ at }: { at: string | null }) {
    const [ahora, setAhora] = useState(() => Date.now())
    useEffect(() => {
        const t = window.setInterval(() => setAhora(Date.now()), 30_000)
        return () => window.clearInterval(t)
    }, [])
    const v = textoVentana(at, ahora)
    return <p className={v.abierta ? 'mt-1 text-xs text-emerald-300' : 'mt-1 text-xs text-amber-300'}>{v.texto}</p>
}

function RelojEspera({ at }: { at: string }) {
    const [ahora, setAhora] = useState(() => Date.now())
    useEffect(() => {
        const t = window.setInterval(() => setAhora(Date.now()), 30_000)
        return () => window.clearInterval(t)
    }, [])
    return <>{llevaEsperando(at, ahora)}</>
}

function HiloElevada({
    accountId,
    conversationId,
    onEnviado,
    onClienteAt,
}: {
    accountId: string
    conversationId: number
    onEnviado: () => void
    onClienteAt?: (iso: string | null) => void
}) {
    const q = useQuery<Hilo>({
        queryKey: ['business-conversation', Number(accountId), conversationId],
        queryFn: () =>
            api.get<Hilo>(`/business/${accountId}/conversations/${conversationId}`),
    })
    useRefrescoHiloAbierto(() => {
        void q.refetch()
    })
    const clienteAt = ultimoClienteDeMensajes(q.data?.messages.status === 'ok' ? (q.data.messages.data ?? []) : [])
    useEffect(() => {
        onClienteAt?.(clienteAt)
    }, [clienteAt, onClienteAt])
    if (q.isPending) return <p className="p-4 text-sm text-neutral-500">Cargando el hilo…</p>
    if (q.isError || !q.data) {
        return <p className="p-4 text-sm text-rose-400">No se pudo abrir esta conversación.</p>
    }

    return (
        <section className="flex min-w-0 flex-col gap-3 bg-neutral-950/80 p-4">
            {q.data.chatwootUrl && (
                <div className="flex justify-end">
                    <a
                        href={q.data.chatwootUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-sky-400 hover:text-sky-300"
                    >
                        Chatwoot ↗
                    </a>
                </div>
            )}
            <MensajesConversacion
                sobre={q.data.messages}
                onReintentar={() => void q.refetch()}
                recargando={q.isFetching}
                compacto
                idHilo={conversationId}
            />
            <ControlDelHilo
                accountId={Number(accountId)}
                conversationId={conversationId}
                mando={q.data.mando}
                onHecho={() => {
                    void q.refetch()
                    onEnviado()
                }}
            />
        </section>
    )
}
